import React, { useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { GetAccess, GetRole, CheckEditAction, CheckDeleteAction } from "../../../../Components/Utils/roles";
import { useToast } from "../../../../Components/Others/ToastServices";
import { useDeleteData } from "../../../../Components/Hooks/useDeleteData";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import NuAmount from "../../../../Components/Utils/NuAmount";
import PremiumTable, { CopyButton } from "../../../../Components/Others/PremiumTable";

// Carrier Renderer
const renderCarrier = (agentName, trackingNumber) => {
  const agent = String(agentName || "").trim().toLowerCase();

  let logoElement = null;
  if (agent.includes("fedex")) {
    logoElement = (
      <div className="flex items-center select-none" style={{ fontFamily: '"Arial Black", Arial, sans-serif' }}>
        <span className="text-[#4D148C] font-black text-[15px] tracking-tight">Fed</span>
        <span className="text-[#FF6200] font-black text-[15px] tracking-tight">Ex</span>
      </div>
    );
  } else if (agent.includes("dhl")) {
    logoElement = (
      <div className="bg-[#FFCC00] text-[#D4001A] font-black italic px-2 py-0.5 rounded text-[11px] tracking-widest border border-[#D4001A]/20 shadow-sm select-none">
        DHL
      </div>
    );
  } else if (agent.includes("ups")) {
    logoElement = (
      <div className="flex items-center gap-1 font-bold text-[#351C15] select-none text-[12px]">
        <svg className="w-4.5 h-4.5 text-[#FFC72C] fill-current" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
        </svg>
        <span>UPS</span>
      </div>
    );
  } else {
    logoElement = (
      <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded">
        {agentName || "N/A"}
      </span>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      {logoElement}
      {trackingNumber && (
        <div className="flex items-center text-[14px] text-gray-800 font-mono mt-0.5">
          <span>{trackingNumber}</span>
          <CopyButton text={trackingNumber} />
        </div>
      )}
    </div>
  );
};

// Shipping Method Renderer
const renderShippingMethod = (method) => {
  const m = String(method || "").trim().toLowerCase();
  const isAir = m.includes("air") || m.includes("plane") || m.includes("fly");
  const isSea = m.includes("sea") || m.includes("ship") || m.includes("ocean") || m.includes("boat");
  const isRoad = m.includes("road") || m.includes("truck") || m.includes("land") || m.includes("car");

  return (
    <div className="flex items-center gap-1.5 text-gray-700 font-semibold text-xs select-none">
      {isAir ? (
        <svg className="w-3.5 h-3.5 text-blue-500 fill-current" viewBox="0 0 24 24">
          <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
        </svg>
      ) : isSea ? (
        <svg className="w-3.5 h-3.5 text-blue-500 fill-current" viewBox="0 0 24 24">
          <path d="M20 21c-1.39 0-2.78-.47-4-1.32-2.44 1.71-5.56 1.71-8 0C6.78 20.53 5.39 21 4 21H2v2h2c1.38 0 2.74-.35 4-.99 2.52 1.29 5.48 1.29 8 0 1.26.65 2.62.99 4 .99h2v-2h-2zM3.93 11.53l.86 9.47h14.42l.86-9.47L12 8l-8.07 3.53zM12 6c.04 0 .07-.01.1-.01L12 2l-4 3 4 1z" />
        </svg>
      ) : isRoad ? (
        <svg className="w-3.5 h-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      )}
      <span>{method || "N/A"}</span>
    </div>
  );
};

// Date Renderer
const renderDate = (dateVal) => {
  if (!dateVal || dateVal === "None") return <span className="text-gray-400">-</span>;
  const formatted = moment(dateVal).format("DD-MMM-YYYY");

  return (
    <div className="flex items-center gap-1.5 text-gray-700 text-xs font-semibold">
      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <span>{formatted}</span>
    </div>
  );
};

// Helper to get active shipment theme
const getShipmentTheme = (status) => {
  const s = String(status || "").trim().toLowerCase();
  if (s.includes("delivered")) return "green";
  if (s.includes("received") || s.includes("warehouse")) return "blue";
  return "yellow";
};

// Reusable Shipment Status Badge Renderer
const renderShipmentStatus = (status) => {
  const theme = getShipmentTheme(status);

  const themes = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200/50 shadow-sm",
    blue: "bg-blue-50 text-blue-700 border-blue-200/50 shadow-sm",
    yellow: "bg-amber-50 text-amber-700 border-amber-200/50 shadow-sm",
  };

  const colorClass = themes[theme] || themes.yellow;

  return (
    <span className={`inline-flex items-center text-center justify-center px-3.5 py-1 rounded-full text-[11px] font-bold border leading-snug tracking-wide max-w-[170px] ${colorClass}`}>
      {status || "N/A"}
    </span>
  );
};

// Timeline Container Component
const ProgressTimeline = ({ tracker, createdAt, theme = "green" }) => {
  const stepsByOrder = Object.fromEntries(
    tracker?.map((step) => [step.order, step]) || []
  );

  const isStepActiveFast = (order, statuses = []) =>
    statuses.includes(stepsByOrder?.[order]?.status);

  const rawActive9 = isStepActiveFast(9, ["PO placed with Supplier"]);
  const rawActive10 = isStepActiveFast(10, ["Order Shipped"]);
  const rawActive11 = isStepActiveFast(11, ["Order Under Customers clearance"]);
  const rawActive12 = isStepActiveFast(12, ["Order Received at Warehouse"]);
  const rawActive13 = isStepActiveFast(13, [
    "Delivered to Customer",
    "Delivered\u00a0to\u00a0Customer",
  ]);

  const isActive13 = rawActive13 || stepsByOrder?.[13]?.date;
  const isActive12 = rawActive12 || stepsByOrder?.[12]?.date || isActive13;
  const isActive11 = rawActive11 || stepsByOrder?.[11]?.date || isActive12;
  const isActive10 = rawActive10 || stepsByOrder?.[10]?.date || isActive11;
  const isActive9 = true;

  // Icons
  const docIcon = (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
  const truckIcon = (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1" />
    </svg>
  );
  const shieldIcon = (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
  const warehouseIcon = (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
  const userIcon = (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  const steps = [
    { title: "PO Placed", active: isActive9, date: stepsByOrder?.[9]?.date || createdAt, icon: docIcon },
    { title: "Shipped", active: isActive10, date: stepsByOrder?.[10]?.date, icon: truckIcon },
    { title: "Under Clearance", active: isActive11, date: stepsByOrder?.[11]?.date, icon: shieldIcon },
    { title: "At Warehouse", active: isActive12, date: stepsByOrder?.[12]?.date, icon: warehouseIcon },
    { title: "Delivered to Customer", active: isActive13, date: stepsByOrder?.[13]?.date, icon: userIcon },
  ];

  let activeCount = 0;
  if (isActive9) activeCount = 1;
  if (isActive10) activeCount = 2;
  if (isActive11) activeCount = 3;
  if (isActive12) activeCount = 4;
  if (isActive13) activeCount = 5;

  const activePercent = activeCount > 1 ? ((activeCount - 1) / 4) * 100 : 0;
  const lastActiveIdx = activeCount - 1;


  return (
    <div className="w-full overflow-x-auto">
      <div className="relative py-4 pl-4 pr-10 w-max min-w-full">
        {/* Connecting Line Track Container */}
        <div className="absolute top-[38px] left-[38px] right-[62px] z-0 pointer-events-none">
          <div className="relative w-full h-[2px]">
            {/* Background connecting line */}
            <div className="absolute left-0 right-0 top-0 bottom-0 bg-gray-200" />

            {/* Active progress line */}
            <div
              className="absolute left-0 top-0 bottom-0 bg-green-500 transition-all duration-500"
              style={{ width: `${activePercent}%` }}
            />
          </div>
        </div>

        {/* Row of Step Columns */}
        <div className="relative flex justify-between items-start w-full min-w-max z-10">
          {steps.map((step, idx) => {
            const formattedDate = step.active && step.date ? moment(step.date).format("DD-MMM-YYYY") : "--";
            const formattedTime = step.active && step.date ? moment(step.date).format("hh:mm A") : "--";

            // Alignment classes based on position to keep edges clean
            let alignClass = "items-center text-center";
            if (idx === 0) {
              alignClass = "items-start text-left";
            } else if (idx === 4) {
              alignClass = "items-end text-right";
            }
            return (
              <div key={idx} className={`flex flex-col w-[120px] shrink-0 ${alignClass}`}>
                {/* Circle container of height 11 (44px) */}
                <div className="h-11 w-11 flex items-center justify-center mb-2 relative">
                  {idx === lastActiveIdx && (
                    <div
                      className="absolute w-9 h-9 rounded-full bg-green-900 animate-ping z-0 pointer-events-none"
                    />
                  )}
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 z-10 transition-all duration-300 ${step.active
                      ? " border-baseColor-primary bg-baseColor-primary text-white shadow-[0_0_10px_rgba(22,163,74,0.25)]"
                      : "bg-white border-gray-200 text-gray-400"
                      }`}
                  >
                    {step.icon}
                  </div>
                </div>

                {/* Label and Date */}
                <p className={`text-xs font-semibold leading-snug ${step.active ? "text-gray-800" : "text-gray-400"}`}>
                  {step.title}
                </p>
                <p className="text-xs text-gray-600 font-semibold mt-1 leading-tight">
                  {formattedDate}
                </p>
                <p className="text-xs text-gray-600 font-semibold leading-tight">
                  {formattedTime}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Main Shipment Table Row Component
const ShipmentTableRow = ({
  row,
  isSelected,
  onSelect,
  pageIndex,
  searchQuery,
  fetcher,
  index,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("shipment_tracking_edit") || GetRole();
  const canDelete = GetAccess("shipment_tracking_delete") || GetRole();

  // const isEven = index % 2 === 0;
  // const bgClass = isEven ? "bg-white" : "bg-[#F8FAFC]/90";
  // const hoverClass = isEven ? "hover:bg-slate-100/40" : "hover:bg-slate-200/30";

  const bgClass = "bg-white";
  const hoverClass = "hover:bg-white/80";
  const handleConfirmDelete = () => {
    handleDeleteData(`/shipping/${row.shippingId}`, setDeleteConfirmOpen);
  };

  return (
    <>
      <tr className={`${bgClass} ${hoverClass} transition-colors align-middle`}>
        {/* Checkbox */}
        <td className="p-4 text-center align-middle border-t border-l border-gray-200/60 rounded-tl-xl">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onSelect}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4 transition duration-150 ease-in-out cursor-pointer"
          />
        </td>

        {/* Shipment ID */}
        <td className="p-4 align-middle border-t border-gray-200/60">
          <div className="flex items-center text-base font-semibold text-blue-600">
            <Link
              to={`/shipping/shipment-tracking/view/${row.shippingId}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="hover:underline text-base text-gray-800"
            >
              {row.prRef || row.ref || "N/A"}
            </Link>
            <CopyButton text={row.prRef || row.ref} />
          </div>

          {/* PO Number */}
          <div className="flex items-center text-base font-semibold text-blue-400">
            <Link
              to={`/shipping/shipment-tracking/view/${row.shippingId}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="hover:underline text-base"
            >
              {row.poRef || "N/A"}
            </Link>
            <CopyButton text={row.poRef} />
          </div>
        </td>


        {/* <td className="p-4 align-middle">

        </td> */}

        {/* Carrier & Tracking */}
        <td className="p-4 align-middle border-t border-gray-200/60">
          {renderCarrier(row.agentName, row.shipmentBill)}
        </td>

        {/* Shipping Method */}
        <td className="p-4 align-middle border-t border-gray-200/60">
          {renderShippingMethod(row.modeOfShipment)}
          {row.bookingDate && (
            <div className="mt-1.5">
              {/* <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Booking Date</span> */}
              <div>{renderDate(row.bookingDate)}</div>
            </div>
          )}
        </td>

        {/* Ship Date */}
        <td className="p-4 align-middle border-t border-gray-200/60">
          {renderDate(row.ETD)}
        </td>

        {/* Est. Delivery */}
        <td className="p-4 align-middle border-t border-gray-200/60">
          {renderDate(row.ETA)}
        </td>

        {/* Amount */}
        <td className="p-4 align-middle border-t border-gray-200/60">
          <div className="flex flex-col text-xs font-semibold">
            <span className="text-green-600 flex items-center gap-1 text-[13px]">
              <NuAmount currency={row.vendorCurrency || row.currency} value={row.ASC || 0} />
              <span className="text-[10px] font-normal text-green-600/80">(Actual)</span>
            </span>
            <span className="text-gray-600 flex items-center gap-1 font-semibold mt-0.5 text-[12px]">
              <NuAmount currency={row.vendorCurrency || row.currency} value={row.ESC || 0} />
              <span className="text-[10px] text-gray-500">(Est.)</span>
            </span>
            {(() => {
              const asc = Number(row.ASC || 0);
              const esc = Number(row.ESC || 0);
              const diff = asc - esc;
              if (asc === 0 || esc === 0 || diff === 0) return null;
              return diff < 0 ? (
                <span className="text-[10px] text-emerald-600 font-bold mt-0.5 flex items-center gap-0.5">
                  Diff: <NuAmount currency={row.vendorCurrency || row.currency} value={Math.abs(diff)} />
                </span>
              ) : (
                <span className="text-[10px] text-red-500 font-bold mt-0.5 flex items-center gap-0.5">
                  Diff: <NuAmount currency={row.vendorCurrency || row.currency} value={diff} />
                </span>
              );
            })()}
          </div>
        </td>

        {/* Current Status Card */}
        <td className="p-4 text-center align-middle border-t border-gray-200/60">
          <div className="flex justify-center">
            {renderShipmentStatus(row.shipmentStatus)}
          </div>
        </td>

        {/* Action Buttons */}
        <td className="p-4 text-center align-middle border-t border-r border-gray-200/60 rounded-tr-xl">
          <div className="flex items-start justify-center gap-1.5">
            {/* View */}
            <Link
              to={`/shipping/shipment-tracking/view/${row.shippingId}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="border border-blue-200 text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
              title="View details"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </Link>

            {/* Edit */}
            {CheckEditAction(row, "Shipment Tracking", canEdit) ? (
              <Link
                to={`/shipping/shipment-tracking/edit/${row.shippingId}`}
                state={{ data: row, from: "/shipping/shipment-tracking", pageIndex, searchQuery }}
                className="border border-sky-200 text-[#0ea5e9] hover:bg-sky-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                title="Edit shipment"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            ) : null}

            {/* Delete */}
            {CheckDeleteAction(row, canDelete) ? (
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="border border-red-200 text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                title="Delete shipment"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            ) : null}
          </div>
        </td>
      </tr>

      {/* Expanded Order Flow Timeline Row */}
      <tr className={`${bgClass}`}>
        <td colSpan={10} className="px-2 py-4 bg-[#FAFAFC]/40 rounded-bl-xl rounded-br-xl border-t border-gray-100">
          <ProgressTimeline tracker={row.tracker} createdAt={row.createdAt} />
        </td>
      </tr>

      {/* Individual Delete Warning Modal */}
      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Warning"
          content={`Are you sure you want to remove this shipment tracking record?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </>
  );
};



// Main Shipment Card Component
const ShipmentCard = ({
  row,
  isSelected,
  onSelect,
  pageIndex,
  searchQuery,
  fetcher,
  index,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("shipment_tracking_edit") || GetRole();
  const canDelete = GetAccess("shipment_tracking_delete") || GetRole();

  const handleConfirmDelete = () => {
    handleDeleteData(`/shipping/${row.shippingId}`, setDeleteConfirmOpen);
  };

  const theme = getShipmentTheme(row.shipmentStatus);

  // Card themes classes
  const themeStyles = {
    green: {
      borderLeft: "border-l-4 border-l-emerald-500",
      iconBg: "bg-emerald-50 text-emerald-600",
    },
    blue: {
      borderLeft: "border-l-4 border-l-blue-500",
      iconBg: "bg-blue-50 text-blue-600",
    },
    yellow: {
      borderLeft: "border-l-4 border-l-amber-500",
      iconBg: "bg-amber-50 text-amber-600",
    }
  };

  const styles = themeStyles[theme] || themeStyles.yellow;
  const borderLeftClass = styles.borderLeft;
  const iconBgClass = styles.iconBg;

  const m = String(row.modeOfShipment || "").trim().toLowerCase();
  const isAir = m.includes("air") || m.includes("plane") || m.includes("fly");
  const isSea = m.includes("sea") || m.includes("ship") || m.includes("ocean") || m.includes("boat");
  const isRoad = m.includes("road") || m.includes("truck") || m.includes("land") || m.includes("car");

  let topIcon = (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
  );

  if (isAir) {
    topIcon = (
      <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
        <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
      </svg>
    );
  } else if (isSea) {
    topIcon = (
      <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
        <path d="M20 21c-1.39 0-2.78-.47-4-1.32-2.44 1.71-5.56 1.71-8 0C6.78 20.53 5.39 21 4 21H2v2h2c1.38 0 2.74-.35 4-.99 2.52 1.29 5.48 1.29 8 0 1.26.65 2.62.99 4 .99h2v-2h-2zM3.93 11.53l.86 9.47h14.42l.86-9.47L12 8l-8.07 3.53zM12 6c.04 0 .07-.01.1-.01L12 2l-4 3 4 1z" />
      </svg>
    );
  } else if (isRoad) {
    topIcon = (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1" />
      </svg>
    );
  }

  return (
    <div className={`relative border border-y-gray-200/50 border-r-gray-200/50 border-l-0 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300 rounded-2xl bg-white p-2 lg:p-5 flex flex-col overflow-hidden ${borderLeftClass}`}>
      {/* Top Header Row of Card */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pb-2 w-full">
        {/* Left Group: Icon, Ref details */}
        <div className="flex items-center justify-between w-full sm:w-auto gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${iconBgClass}`}>
              {topIcon}
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <Link
                  to={`/shipping/shipment-tracking/view/${row.shippingId}`}
                  state={{ data: row, pageIndex, searchQuery }}
                  className="text-gray-800 font-bold hover:underline text-[14px]"
                >
                  {row.prRef || "N/A"}
                </Link>
                <CopyButton text={row.prRef} />
              </div>
              <div className="flex items-center text-xs font-semibold text-blue-500 mt-0.5">
                <Link
                  to={`/shipping/shipment-tracking/view/${row.shippingId}`}
                  state={{ data: row, pageIndex, searchQuery }}
                  className="hover:underline text-[12px]"
                >
                  {row.poRef || "N/A"}
                </Link>
                <CopyButton text={row.poRef} />
              </div>
            </div>
          </div>

          {/* Status badge on mobile screens */}
          <div className="sm:hidden shrink-0">
            {renderShipmentStatus(row.shipmentStatus)}
          </div>
        </div>

        {/* Right Group: Carrier Tracking & Status Card */}
        <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto">
          {/* Carrier Tracking */}
          <div className="shrink-0 scale-95 origin-left sm:origin-center">
            {renderCarrier(row.agentName, row.shipmentBill)}
          </div>

          {/* Status Card on sm+ screens */}
          <div className="hidden sm:block shrink-0 scale-95 origin-right">
            {renderShipmentStatus(row.shipmentStatus)}
          </div>
        </div>
      </div>

      {/* Middle Panel Grid */}
      <div className="rounded-xl py-2 border-t flex flex-col sm:flex-row justify-between items-start px-2 gap-2">
        {/* Ship Date */}
        <div className="flex flex-col gap-1 flex-1">
          <span className="text-[10px] font-semibold text-gray-600 flex items-center gap-1 select-none">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Ship Date
          </span>
          <span className="text-[12px] font-bold text-gray-700 leading-tight">
            {row.ETD ? moment(row.ETD).format("DD-MMM-YYYY") : "--"}
          </span>
        </div>

        {/* Divider */}
        <div className="w-[1px] h-8 bg-gray-100 hidden sm:block" />

        {/* Est. Delivery */}
        <div className="flex flex-col gap-1 flex-1 sm:pl-2">
          <span className="text-[10px] font-semibold text-gray-600 flex items-center gap-1 select-none">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Est. Delivery
          </span>
          <span className="text-[12px] font-bold text-gray-700 leading-tight">
            {row.ETA ? moment(row.ETA).format("DD-MMM-YYYY") : "--"}
          </span>
        </div>

        {/* Divider */}
        <div className="w-[1px] h-8 bg-gray-100 hidden sm:block" />

        {/* Amount */}
        <div className="flex flex-col gap-0.5 flex-1 sm:pl-2">
          <span className="text-[10px] font-semibold text-gray-600 select-none">Amount</span>
          <span className="text-green-600 flex items-center gap-1 text-[13px] font-bold leading-none">
            <NuAmount currency={row.vendorCurrency || row.currency} value={row.ASC || 0} />
            <span className="text-[9px] font-normal text-green-600/80">(Actual)</span>
          </span>
          <span className="text-gray-600 flex items-center gap-1 font-semibold text-[11px] leading-none mt-0.5">
            <NuAmount currency={row.vendorCurrency || row.currency} value={row.ESC || 0} />
            <span className="text-[9px] text-gray-500">(Est.)</span>
          </span>
          {(() => {
            const asc = Number(row.ASC || 0);
            const esc = Number(row.ESC || 0);
            const diff = asc - esc;
            if (asc === 0 || esc === 0 || diff === 0) return null;
            return diff < 0 ? (
              <span className="text-[9px] text-[#10b981] font-bold leading-none">
                Diff: <NuAmount currency={row.vendorCurrency || row.currency} value={Math.abs(diff)} />
              </span>
            ) : (
              <span className="text-[9px] text-red-500 font-bold leading-none">
                Diff: <NuAmount currency={row.vendorCurrency || row.currency} value={diff} />
              </span>
            );
          })()}
        </div>
      </div>

      {/* Progress Timeline */}
      <div className="">
        <ProgressTimeline tracker={row.tracker} createdAt={row.createdAt} theme={theme} />
      </div>

      {/* Action Buttons Row */}
      <div className="flex items-center justify-between border-t border-gray-200 pt-2 mt-1">
        {/* View Details */}
        <Link
          to={`/shipping/shipment-tracking/view/${row.shippingId}`}
          state={{ data: row, pageIndex, searchQuery }}
          className="border border-gray-200 text-gray-600 hover:bg-gray-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
          title="View details"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span className="hidden sm:inline">View Details</span>
        </Link>

        {/* Edit & Delete */}
        <div className="flex items-center gap-1.5">
          {CheckEditAction(row, "Shipment Tracking", canEdit) ? (
            <Link
              to={`/shipping/shipment-tracking/edit/${row.shippingId}`}
              state={{ data: row, from: "/shipping/shipment-tracking", pageIndex, searchQuery }}
              className="border border-sky-100 text-[#0ea5e9] hover:bg-sky-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
              title="Edit"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <span className="hidden sm:inline">Edit</span>
            </Link>
          ) : null}

          {CheckDeleteAction(row, canDelete) ? (
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="border border-red-100 text-red-500 hover:bg-red-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
              title="Delete"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="hidden sm:inline">Delete</span>
            </button>
          ) : null}
        </div>
      </div>

      {/* Individual Delete Warning Modal */}
      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Warning"
          content={`Are you sure you want to remove this shipment tracking record?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </div>
  );
};

// Main ShipmentTrackingTable Component
const ShipmentTrackingTable = ({
  data = [],
  total = 0,
  pageIndex,
  setPageIndex,
  dataCount,
  setDataCount,
  searchQuery,
  listofNumbers = [],
  selectOneFunction,
  selectMultipleFunction,
  fullList = [],
  fetcher,
  dataLoading = false,
  showEdit = false,
  setShowEdit,
  viewMode = "table",
}) => {
  const headers = [
    { title: "YPR / PO" },
    { title: "Shipping Agent" },
    { title: "Shipping Method" },
    { title: "ETD" },
    { title: "ETA" },
    { title: "Shipment Cost" },
    { title: "Status", align: "center" },
    { title: "Action", align: "center" },
  ];

  return (
    <PremiumTable
      data={data}
      total={total}
      pageIndex={pageIndex}
      setPageIndex={setPageIndex}
      dataCount={dataCount}
      setDataCount={setDataCount}
      viewMode={viewMode}
      headers={headers}
      renderRow={(row, idx) => (
        <ShipmentTableRow
          row={row}
          isSelected={listofNumbers.includes(row.shippingId)}
          onSelect={() => selectOneFunction(row.shippingId)}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
          index={idx}
        />
      )}
      renderCard={(row, idx) => (
        <ShipmentCard
          row={row}
          isSelected={listofNumbers.includes(row.shippingId)}
          onSelect={() => selectOneFunction(row.shippingId)}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
          index={idx}
        />
      )}
      dataLoading={dataLoading}
      listofNumbers={listofNumbers}
      selectMultipleFunction={selectMultipleFunction}
      fullList={fullList}
      showEdit={showEdit}
      setShowEdit={setShowEdit}
      multiEditLabel="Multi Edit"
    />
  );
};

export default ShipmentTrackingTable;
